import { supabase } from '@/integrations/supabase/client';
import { convertToWebP, isConvertibleToWebP, getOptimalConversionSettings } from '@/utils/image-conversion';
import type { PrivateImageGeneratorItem } from '@/hooks/private-image-generator/types';
import { ImageType } from '@/components/image-generator/FilterBar/TypeFilter';

/**
 * Parameters for generating a private image
 */
export interface GeneratePrivateImageParams {
  // Core parameters
  description: string;
  width: number;
  height: number;
  userId: string;
  
  // Optional style parameters
  imageType?: ImageType | null;
  styleId?: string | null;
  influenceId?: string | null;
  backgroundId?: string | null;
  environmentIds?: string[];
  moodIds?: string[];
  colorIds?: string[];
  
  // Optional project assignment
  workbookId?: string | null;
  
  // Function dependencies
  executeAIOperation: (
    operation: () => Promise<any>,
    options?: { immediate?: boolean; errorPrefix?: string; debug?: boolean }
  ) => Promise<any>;
  
  // Callbacks
  onProgress?: (message: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Result from image generation
 */
export interface GeneratePrivateImageResult {
  imageUrl: string;
  savedImage: PrivateImageGeneratorItem;
  metadata: {
    prompt: string;
    dimensions: { width: number; height: number };
    style: string | null;
    type: string | null;
  };
}

/**
 * Sanitizes a filename for storage
 */
const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/\.[^/.]+$/, '')
    .replace(/[^a-zA-Z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);
};

/**
 * Gets image dimensions from a file
 */
const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
    };
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Maps frontend image type strings to backend IDs
 */
const getImageTypeId = (type: ImageType | null | undefined): string | undefined => {
  if (!type) return undefined;
  
  const typeMap: Record<string, string> = {
    'Photo': 'type-photo',
    'Art': 'type-art', 
    'Anime': 'type-anime',
    '3D': 'type-3d'
  };
  
  return typeMap[type];
};

/**
 * Core function to generate a private image with full metadata
 * This is the single source of truth for all private image generation
 * 
 * @param params - Generation parameters
 * @returns Generated image data with storage URL and metadata
 */
export async function generatePrivateImage(
  params: GeneratePrivateImageParams
): Promise<GeneratePrivateImageResult> {
  const {
    description,
    width,
    height,
    userId,
    imageType,
    styleId,
    influenceId,
    backgroundId,
    environmentIds = [],
    moodIds = [],
    colorIds = [],
    workbookId = null,
    executeAIOperation,
    onProgress,
    onError
  } = params;

  try {
    // Validate required parameters
    if (!description.trim()) {
      throw new Error('Image description is required');
    }

    if (!userId) {
      throw new Error('User ID is required');
    }

    onProgress?.('Starting image generation...');

    // Map frontend type to backend ID
    const imageTypeId = getImageTypeId(imageType);
    
    console.log('[generatePrivateImage] Starting generation with params:', {
      description,
      imageType,
      imageTypeId,
      styleId,
      dimensions: { width, height }
    });

    // Call generate-image edge function
    const data = await executeAIOperation(
      async () => {
        const { data, error } = await supabase.functions.invoke('generate-image', {
          body: {
            additionalText: description,
            imageType: imageTypeId,
            styleId: styleId || undefined,
            influenceId: influenceId || undefined,
            backgroundId: backgroundId || undefined,
            environmentIds: environmentIds.length > 0 ? environmentIds : undefined,
            moodIds: moodIds.length > 0 ? moodIds : undefined,
            colorIds: colorIds.length > 0 ? colorIds : undefined,
            width: width,
            height: height,
            numberResults: 1,
            format: 'webp',
            quality: 90,
            appName: 'Private Image Generator',
            functionName: 'Private Image Generation',
          },
        });

        if (error) {
          console.error('[generatePrivateImage] Edge function error:', error);
          throw new Error(`Image generation failed: ${error.message}`);
        }

        if (!data || data.error) {
          throw new Error(data?.error || 'Image generation failed');
        }

        return data;
      },
      {
        immediate: true,
        errorPrefix: 'Private image generation failed',
        debug: false
      }
    );
    
    // Extract image data from response
    if (!data.data || !Array.isArray(data.data)) {
      throw new Error('Invalid response format from image generation');
    }

    const imageResults = data.data.filter((item: any) => item.taskType === 'imageInference');
    
    if (imageResults.length === 0) {
      throw new Error('No images were generated');
    }

    const runwareImageUrl = imageResults[0].imageURL;
    
    if (!runwareImageUrl) {
      throw new Error('No image URL returned from generation');
    }

    console.log('[generatePrivateImage] Image generated successfully:', runwareImageUrl);
    onProgress?.('Image generated, saving to storage...');

    // Download the image from Runware URL
    const response = await fetch(runwareImageUrl);
    if (!response.ok) {
      throw new Error(`Failed to download image: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    const file = new File([blob], 'generated-image.webp', { type: 'image/webp' });

    // Process the file (WebP conversion if needed)
    let processedFile = file;
    let conversionInfo: string | null = null;

    if (file.type !== 'image/webp' && isConvertibleToWebP(file)) {
      try {
        const conversionSettings = getOptimalConversionSettings(file, 'workbook');
        const result = await convertToWebP(file, conversionSettings);
        processedFile = result.file;
        conversionInfo = `Converted to WebP (${result.compressionRatio.toFixed(1)}% size reduction)`;
        console.log('[generatePrivateImage] WebP conversion successful:', conversionInfo);
      } catch (error) {
        console.warn('[generatePrivateImage] WebP conversion failed, using original:', error);
      }
    }

    // Create filename
    const timestamp = Date.now();
    const sanitizedPrompt = sanitizeFilename(description);
    const prefix = workbookId ? 'project' : 'private';
    const baseFilename = `${prefix}_generated_${sanitizedPrompt}_${timestamp}`;
    const filename = `${baseFilename}.webp`;
    const storagePath = `${userId}/workbook_images/${filename}`;

    console.log('[generatePrivateImage] Uploading to storage:', { storagePath, fileSize: processedFile.size });

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from('workbook_images')
      .upload(storagePath, processedFile, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('[generatePrivateImage] Storage upload failed:', uploadError);
      throw new Error(`Failed to upload image: ${uploadError.message}`);
    }

    // Get the public URL
    const { data: urlData } = supabase.storage
      .from('workbook_images')
      .getPublicUrl(storagePath);

    const publicUrl = urlData.publicUrl;
    console.log('[generatePrivateImage] Got public URL:', publicUrl);

    // Get actual image dimensions
    const dimensions = await getImageDimensions(processedFile);
    const finalWidth = dimensions.width || width;
    const finalHeight = dimensions.height || height;

    // Build filter metadata for structured querying
    const filterMetadata = {
      styleId: styleId || null,
      influenceId: influenceId || null,
      backgroundId: backgroundId || null,
      environmentIds: environmentIds.length > 0 ? environmentIds : null,
      moodIds: moodIds.length > 0 ? moodIds : null,
      colorIds: colorIds.length > 0 ? colorIds : null,
      imageType: imageType || null,
      width: finalWidth,
      height: finalHeight,
    };

    // Save to database
    const { data: dbData, error: dbError } = await supabase
      .from('workbook_images')
      .insert({
        user_id: userId,
        workbook_id: workbookId,
        page_id: null,
        image_url: publicUrl,
        storage_path: storagePath,
        filename: filename,
        file_size: processedFile.size,
        width: finalWidth,
        height: finalHeight,
        alt_text: description,
        generation_prompt: description,
        is_generated: true,
        filter_metadata: filterMetadata, // Store filter selections for precise querying
      })
      .select()
      .single();

    if (dbError) {
      console.error('[generatePrivateImage] Database insert failed:', dbError);
      // Cleanup uploaded file if database insert fails
      await supabase.storage.from('workbook_images').remove([storagePath]);
      throw new Error(`Failed to save image metadata: ${dbError.message}`);
    }

    console.log('[generatePrivateImage] Image saved to database successfully:', dbData.id);

    // Transform to PrivateImageGeneratorItem interface
    const savedImage: PrivateImageGeneratorItem = {
      id: dbData.id,
      user_id: dbData.user_id,
      workbook_id: dbData.workbook_id,
      page_id: null,
      image_url: dbData.image_url,
      storage_path: dbData.storage_path,
      filename: dbData.filename,
      file_size: dbData.file_size,
      width: dbData.width,
      height: dbData.height,
      alt_text: dbData.alt_text,
      generation_prompt: dbData.generation_prompt,
      is_generated: dbData.is_generated,
      created_at: dbData.created_at,
      updated_at: dbData.updated_at,
      deleted_at: null,
    };

    console.log('[generatePrivateImage] Image saved successfully:', savedImage.id);
    onProgress?.('Image saved successfully!');

    // Return comprehensive result
    return {
      imageUrl: savedImage.image_url,
      savedImage,
      metadata: {
        prompt: description,
        dimensions: { width, height },
        style: styleId,
        type: imageType
      }
    };
  } catch (error) {
    console.error('[generatePrivateImage] Error:', error);
    onError?.(error as Error);
    throw error;
  }
}

/**
 * Simplified interface for AI chat usage
 * Provides sensible defaults for quick image generation
 */
export async function generatePrivateImageSimple({
  description,
  style = 'photo',
  size = '1024x1024',
  userId,
  executeAIOperation
}: {
  description: string;
  style?: string;
  size?: string;
  userId: string;
  executeAIOperation: any;
}): Promise<GeneratePrivateImageResult> {
  // Parse dimensions
  const [width, height] = size.split('x').map(d => parseInt(d.trim()) || 1024);
  
  // Map simple style to image type
  const imageTypeMap: Record<string, ImageType> = {
    'photo': 'Photo',
    'art': 'Art',
    'anime': 'Anime',
    '3d': '3D'
  };
  
  const imageType = imageTypeMap[style.toLowerCase()] || 'Photo';
  
  return generatePrivateImage({
    description,
    width,
    height,
    userId,
    imageType,
    executeAIOperation
  });
}